import type {
  AlbumUserDTO,
  AnchorDetailDTO,
  CartoonBaseDTO,
  DistributeFeedItem,
} from '@/api/distribute'
import { DISTRIBUTE_AI_BASE_URL } from '@/constants/distribute'
import { getDreamaDomain } from '@/constants/domain'
import { openLegacyPlayerUrl, type LegacyPlayerOpenOptions } from '@/utils/legacy-player'

interface OpenExternalOptions {
  legacyPlayer?: LegacyPlayerOpenOptions
}

function buildUrl(path: string, queryKey: string, queryValue: string | null | undefined) {
  if (!queryValue) {
    return null
  }

  const url = new URL(path, getDreamaDomain())
  url.searchParams.set(queryKey, queryValue)

  return url.toString()
}

export function buildLandingUrl(item: DistributeFeedItem): string | null {
  if (item.obj === 'radio' || item.func === 'radio') {
    return buildUrl('/radio.html', 'radio_id', item.id)
  }

  if (item.obj === 'pipe') {
    return buildUrl('/view.html', 'pipe_id', item.id)
  }

  if (item.obj === 'media') {
    return buildUrl('/rolematerial.html', 'cartoonBoxId', item.id)
  }

  return null
}

export function buildUserUrl(user: AlbumUserDTO | null | undefined): string | null {
  return user?.uid ? `/${encodeURIComponent(user.uid)}` : null
}

export function buildCartoonUrl(
  cartoon: Pick<CartoonBaseDTO, 'id' | 'type'> | null | undefined,
): string | null {
  if (!cartoon?.id) {
    return null
  }

  const path = cartoon.type?.trim().toLowerCase() === 'cloned' ? '/user' : '/character'

  return `${path}/${encodeURIComponent(cartoon.id)}`
}

export function buildAnchorUrl(anchor: AnchorDetailDTO | null | undefined): string | null {
  const jump = anchor?.jump?.trim()
  const id = anchor?.id

  if (!jump) {
    return null
  }

  switch (jump) {
    case 'pipe':
    case 'room':
    case 'storyline':
      return buildUrl('/view.html', 'pipe_id', id)
    case 'radio':
      return buildUrl('/radio.html', 'radio_id', id)
    case 'cartoon_box':
      return buildUrl('/rolematerial.html', 'cartoonBoxId', id)
    case 'cartoon':
    case 'subscription':
    case 'cartoon_rank':
    case 'cartoon_reward':
      return id ? `/character/${encodeURIComponent(id)}` : null
    case 'createCenter':
    case 'draft_box':
    case 'editContent':
    case 'creation_media_prompt':
    case 'creation_comic_prompt':
    case 'media_mv':
    case 'media_mv_tpl':
    case 'creation_music':
    case 'storyShelf':
    case 'createClonePage':
    case 'createCartoon':
      return DISTRIBUTE_AI_BASE_URL
    default:
      return null
  }
}

export function openExternal(url: string | null | undefined, options: OpenExternalOptions = {}) {
  if (!url || typeof window === 'undefined') {
    return
  }

  if (openLegacyPlayerUrl(url, options.legacyPlayer)) {
    return
  }

  window.open(url, '_blank', 'noopener,noreferrer')
}

export function openLanding(item: DistributeFeedItem) {
  openExternal(buildLandingUrl(item), {
    legacyPlayer: item.obj === 'media' ? { forceAutoplaySound: true } : undefined,
  })
}

export function openUser(user: AlbumUserDTO | null | undefined) {
  const url = buildUserUrl(user)

  if (!url || typeof window === 'undefined') {
    return
  }

  window.location.assign(url)
}

export function openCartoon(cartoon: CartoonBaseDTO | null | undefined) {
  const url = buildCartoonUrl(cartoon)

  if (!url || typeof window === 'undefined') {
    return
  }

  window.location.assign(url)
}

export function openAnchor(anchor: AnchorDetailDTO | null | undefined) {
  openExternal(buildAnchorUrl(anchor))
}
